import { Box, Collapse, Heading, Link, Text } from '@ksyos/design-system';
import { useState } from 'react';
import { styled } from 'styled-components';
import type { Color } from '@frontend/components/pages/services/workflowGraph/helpers';
import { getColorCode } from '@frontend/components/pages/services/workflowGraph/helpers';

type LegendItemData = { bgColor: Color; borderColor?: Color; label: string };

const steps: LegendItemData[] = [
    { bgColor: 'green', label: 'Finished' },
    { bgColor: 'yellow', label: 'In Progress' },
    { bgColor: 'red', label: 'Failed' },
    { bgColor: 'lightblue', borderColor: 'blue', label: 'Activity' },
    { bgColor: 'white', borderColor: 'green', label: 'Available' },
    { bgColor: 'orange', label: 'Scheduled' },
    { bgColor: 'green', borderColor: 'red', label: 'Obsolete' },
    { bgColor: 'grey', label: 'Cancelled' },
    { bgColor: 'white', borderColor: 'purple', label: 'General' },
];

const transitions: LegendItemData[] = [
    { bgColor: 'green', label: 'Finished' },
    { bgColor: 'purple', label: 'Synchronous' },
    { bgColor: 'orange', label: 'Asynchronous' },
    { bgColor: 'red', label: 'Disabled' },
];

export function WorkflowLegend() {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <>
            <Collapse isOpen={isExpanded}>
                <LegendContent />
            </Collapse>
            <Link
                color="subtle"
                fontWeight="normal"
                iconLeft={isExpanded ? 'caret-up' : 'caret-down'}
                my={2}
                onClick={() => setIsExpanded((exp) => !exp)}
                size="xs"
                variant="button"
            >
                {isExpanded ? 'Hide Legend' : 'Show legend'}
            </Link>
        </>
    );
}

function LegendContent() {
    return (
        <Box
            display="flex"
            gap={4}
        >
            <Box>
                <Heading
                    fontWeight="normal"
                    size="small"
                >
                    Steps
                </Heading>
                {steps.map(({ bgColor, borderColor, label }) => (
                    <LegendItem
                        bgColor={bgColor}
                        borderColor={borderColor}
                        key={label}
                        label={label}
                    />
                ))}
            </Box>
            <Box>
                <Heading
                    fontWeight="normal"
                    size="small"
                >
                    Transitions
                </Heading>
                {transitions.map(({ bgColor, borderColor, label }) => (
                    <LegendItem
                        bgColor={bgColor}
                        borderColor={borderColor}
                        key={label}
                        label={label}
                    />
                ))}
            </Box>
        </Box>
    );
}

function LegendItem({ bgColor, borderColor, label }: Readonly<LegendItemData>) {
    return (
        <Text size="sm">
            <StyledIndicator
                $bgColor={bgColor}
                $borderColor={borderColor}
            />
            {label}
        </Text>
    );
}

const StyledIndicator = styled.span<{ $bgColor: Color; $borderColor?: Color }>`
    background-color: ${(p) => getColorCode(p.$bgColor)};
    border: ${(p) => (p.$borderColor ? ' 1px solid ' + p.$borderColor : 'none')};
    display: inline-block;
    height: 10px;
    margin-right: 12px;
    width: 10px;
`;
